// Верхняя полоса 40 px (README §1.1). Одна строка: ☰, прокручиваемые вкладки
// разделов (+ вкладки черновиков), справа всегда видимые «+», «✎», «?»,
// ☀/☾ и аватар. От темы не зависит — свои токены --topbar-*.

import { useEffect, useRef } from 'react'
import { useAppStore } from '../store/appStore'
import type { Section } from '../store/appStore'
import { MAIN_TABS, resolveSection, useDynamicTabs } from '../sections/registry'
import { Icon } from '../ui/Icon'
import './shell.css'

export default function TopBar() {
  const section = useAppStore((s) => s.section)
  const setSection = useAppStore((s) => s.setSection)
  const toggleSide = useAppStore((s) => s.toggleSide)
  const theme = useAppStore((s) => s.theme)
  const toggleTheme = useAppStore((s) => s.toggleTheme)
  const drafts = useAppStore((s) => s.drafts)
  const secEdit = useAppStore((s) => s.secEdit)
  const activeId = useAppStore((s) => s.activeId)
  const focusSession = useAppStore((s) => s.focusSession)
  const newSectionDraft = useAppStore((s) => s.newSectionDraft)
  const editSection = useAppStore((s) => s.editSection)
  const profileLabel = useAppStore((s) => s.profileLabel)
  const profile = useAppStore((s) => s.profile)
  const online = useAppStore((s) => s.online)
  const linkState = useAppStore((s) => s.linkState)

  // пользовательские разделы и серверные метки «черновик» (§10, зона pages)
  const dynamic = useDynamicTabs()
  const current = resolveSection(section)
  const letter = (profileLabel || profile || 'S').slice(0, 1).toUpperCase()
  const live = online && linkState !== 'offline'

  // Активная вкладка всегда в поле зрения полосы — на телефоне она прокручивается.
  const tabsRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const on = tabsRef.current?.querySelector<HTMLElement>('.abx-top__tab.is-on')
    on?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [section, activeId])

  // «?» и аватар работают как переключатели: повторный клик возвращает туда,
  // откуда пришли.
  const back = useRef<Section>('chats')
  useEffect(() => {
    if (section !== 'kb' && section !== 'account') back.current = section
  }, [section])

  const go = (id: Section) => setSection(id)

  return (
    <header className="abx-top">
      <button className="abx-top__burger" title="боковое меню" onClick={toggleSide} aria-label="боковое меню">
        <span />
        <span />
        <span />
      </button>

      <div className="abx-top__tabs" ref={tabsRef} role="tablist">
        {MAIN_TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={section === tab.id}
            className={`abx-top__tab${section === tab.id ? ' is-on' : ''}`}
            onClick={() => go(tab.id as Section)}
          >
            {tab.title}
            {secEdit[tab.id as Section] || dynamic.drafted.has(tab.id) ? <span className="abx-top__draft">черновик</span> : null}
          </button>
        ))}
        {dynamic.tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={section === tab.id}
            className={`abx-top__tab${section === tab.id ? ' is-on' : ''}`}
            onClick={() => go(tab.id)}
          >
            {tab.title}
            {tab.draft || secEdit[tab.id] ? <span className="abx-top__draft">черновик</span> : null}
          </button>
        ))}
        {drafts.map((draft) => {
          // Вкладка черновика: из беседы-сборщика — в предпросмотр черновика,
          // из предпросмотра — обратно в беседу (§10.1, ui-plan п. 2).
          const on = section === draft.id || (section === 'chats' && draft.sessionId !== null && activeId === draft.sessionId)
          return (
            <button
              key={draft.id}
              role="tab"
              aria-selected={on}
              className={`abx-top__tab${on ? ' is-on' : ''}`}
              onClick={() => (section === draft.id && draft.sessionId ? focusSession(draft.sessionId) : go(draft.id))}
            >
              {draft.title}
              <span className="abx-top__draft">черновик</span>
            </button>
          )
        })}
      </div>

      <button
        className="abx-top__btn abx-top__plus"
        title="новый раздел — описать в беседе"
        onClick={() => void newSectionDraft()}
      >
        +
      </button>
      <button
        className="abx-top__btn"
        title={`править раздел «${current.title}»`}
        onClick={() => void editSection(section, current.title)}
      >
        <Icon name="pencil" size={14} stroke={1.7} />
      </button>
      <button
        className={`abx-top__help${section === 'kb' ? ' is-on' : ''}`}
        title="База знаний — справочник и правила"
        onClick={() => go(section === 'kb' ? back.current : 'kb')}
      >
        ?
      </button>
      <button
        className="abx-top__theme"
        title={theme === 'light' ? 'тёмная тема' : 'светлая тема'}
        onClick={toggleTheme}
      >
        {theme === 'light' ? '☾' : '☀'}
      </button>
      <button
        className={`abx-top__avatar${section === 'account' ? ' is-on' : ''}`}
        title={live ? 'кабинет · сервер на связи' : 'кабинет · нет связи с сервером'}
        onClick={() => go(section === 'account' ? back.current : 'account')}
      >
        {letter}
        <span className={`abx-top__dot${live ? '' : ' is-off'}`} />
      </button>
    </header>
  )
}
